import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { TrendingUp, Eye, DollarSign, Award, BarChart3, Users, Lock } from "lucide-react";

const ZONE_LABELS = {
    poitrine: "Poitrine", dos_haut: "Dos haut", dos_bas: "Dos bas",
    epaule_gauche: "Épaule G.", cou_droit: "Cou D.", cou_gauche: "Cou G.",
};

export default function SponsorAnalytics() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/sponsorships/analytics")
            .then((r) => setData(r.data))
            .catch((e) => setError(formatErr(e.response?.data?.detail)));
    }, []);

    if (error) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-16 text-center">
                <Lock size={48} className="mx-auto text-zinc-600 mb-4" />
                <h1 className="font-display text-4xl uppercase tracking-tight mb-3">
                    Analytics Diamond+
                </h1>
                <p className="text-zinc-400 mb-6">{error}</p>
                <Link to="/dashboard/sponsor/loyalty" className="btn-primary">
                    <Award size={16} /> Voir mon programme fidélité
                </Link>
            </div>
        );
    }

    if (!data) return <div className="p-12 text-zinc-500">Chargement…</div>;

    const lvl = data.level;
    const monthlyEntries = Object.entries(data.monthly).sort();
    const maxMonthly = Math.max(1, ...monthlyEntries.map(([, v]) => v));

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 space-y-10">
            <header className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold mb-2" style={{ color: lvl.color }}>
                        Analytics {lvl.label} · réservé Diamond+
                    </div>
                    <h1 className="font-display text-5xl md:text-7xl uppercase tracking-tight leading-none">
                        Votre impact
                    </h1>
                    <p className="text-zinc-400 mt-2">
                        Tableau de bord de visibilité et ROI estimé pour {user.name}.
                    </p>
                </div>
                <Link to="/dashboard/sponsor/loyalty" className="btn-ghost py-2 px-4 text-xs">
                    <Award size={14} /> Programme fidélité
                </Link>
            </header>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-zinc-800 border border-zinc-800">
                <Kpi icon={<Users size={18} />} label="Maillots" value={data.totals.jerseys} />
                <Kpi icon={<DollarSign size={18} />} label="Investi" value={`${data.totals.invested}€`} />
                <Kpi icon={<TrendingUp size={18} />} label="Clubs" value={data.totals.clubs} />
                <Kpi icon={<Eye size={18} />} label="Vues estimées" value={data.estimated_visibility_views.toLocaleString("fr-FR")} />
                <Kpi icon={<BarChart3 size={18} />} label="Coût / vue" value={`${data.avg_cost_per_view}€`} />
            </div>

            {/* By sport */}
            <section>
                <h2 className="font-display text-3xl uppercase tracking-tight mb-4">Par sport</h2>
                <div className="card-tu space-y-3">
                    {Object.keys(data.by_sport).length === 0 ? (
                        <p className="text-zinc-500 text-sm">Aucune donnée</p>
                    ) : Object.entries(data.by_sport)
                        .sort(([, a], [, b]) => b - a)
                        .map(([sport, n]) => {
                            const max = Math.max(...Object.values(data.by_sport));
                            const pct = (n / max) * 100;
                            return (
                                <div key={sport}>
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="text-zinc-300 font-bold">{sport}</span>
                                        <span className="text-orange-500 font-bold">{n} maillots</span>
                                    </div>
                                    <div className="w-full h-2 bg-zinc-900 border border-zinc-800 overflow-hidden">
                                        <div className="neon-bar" style={{ width: `${pct}%` }} />
                                    </div>
                                </div>
                            );
                        })}
                </div>
            </section>

            {/* By zone */}
            <section>
                <h2 className="font-display text-3xl uppercase tracking-tight mb-4">Emplacements préférés</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-zinc-800 border border-zinc-800">
                    {Object.entries(ZONE_LABELS).map(([k, l]) => (
                        <div key={k} className="bg-[#09090b] p-4 text-center">
                            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{l}</div>
                            <div className="font-display text-3xl text-orange-500">{data.by_zone[k] || 0}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Monthly evolution */}
            <section>
                <h2 className="font-display text-3xl uppercase tracking-tight mb-4">Évolution mensuelle</h2>
                <div className="card-tu">
                    {monthlyEntries.length === 0 ? (
                        <p className="text-zinc-500 text-sm">Aucune activité</p>
                    ) : (
                        <div className="flex items-end gap-2 h-40 overflow-x-auto">
                            {monthlyEntries.map(([m, v]) => (
                                <div key={m} className="flex flex-col items-center gap-2 min-w-[48px]">
                                    <div
                                        className="w-full bg-gradient-to-t from-orange-600 to-red-500 transition-all"
                                        style={{ height: `${(v / maxMonthly) * 130}px` }}
                                        title={`${v} maillots`}
                                    />
                                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{m}</div>
                                    <div className="text-[10px] text-orange-500 font-bold">{v}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}

function Kpi({ icon, label, value }) {
    return (
        <div className="bg-[#09090b] p-5">
            <div className="flex items-center gap-2 text-orange-500 mb-2">
                {icon}
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{label}</span>
            </div>
            <div className="font-display text-3xl text-white leading-none">{value}</div>
        </div>
    );
}
